/**
 * WhatsApp's inline formatting: *bold*, _italic_, ~strikethrough~, ```monospace``` and `inline
 * code`. A marker only counts next to text (never next to a space) and at a word edge, as in the
 * app, and never across lines. Returns plain segments so nothing is rendered as HTML.
 */
export interface WaSegment {
  text: string;
  bold?: true;
  italic?: true;
  strike?: true;
  mono?: true;
  code?: true;
}
type Style = "bold" | "italic" | "strike" | "mono" | "code";

const MARKS: Array<{ mark: string; style: Style }> = [
  { mark: "```", style: "mono" },
  { mark: "*", style: "bold" },
  { mark: "_", style: "italic" },
  { mark: "~", style: "strike" },
  { mark: "`", style: "code" },
];

const edge = (ch: string | undefined) =>
  ch === undefined || !/[\p{L}\p{N}]/u.test(ch);
const blank = (ch: string | undefined) => ch === undefined || /\s/.test(ch);

function closing(text: string, mark: string, from: number): number {
  for (
    let j = text.indexOf(mark, from);
    j !== -1;
    j = text.indexOf(mark, j + 1)
  ) {
    if (text.slice(from, j).includes("\n")) return -1;
    if (j > from && !blank(text[j - 1]) && edge(text[j + mark.length]))
      return j;
  }
  return -1;
}

function parse(
  text: string,
  styles: Partial<Record<Style, true>>,
  out: WaSegment[],
): void {
  let plain = "";
  let i = 0;
  const flush = () => {
    if (plain) out.push({ text: plain, ...styles });
    plain = "";
  };
  while (i < text.length) {
    const hit = MARKS.find(
      ({ mark }) =>
        text.startsWith(mark, i) &&
        edge(text[i - 1]) &&
        !blank(text[i + mark.length]),
    );
    const end = hit ? closing(text, hit.mark, i + hit.mark.length) : -1;
    if (!hit || end === -1) {
      plain += text[i];
      i += 1;
      continue;
    }
    flush();
    const inner = text.slice(i + hit.mark.length, end);
    if (hit.style === "mono" || hit.style === "code")
      out.push({ text: inner, ...styles, [hit.style]: true });
    else parse(inner, { ...styles, [hit.style]: true }, out);
    i = end + hit.mark.length;
  }
  flush();
}

export function waSegments(text: string): WaSegment[] {
  const out: WaSegment[] = [];
  parse(text, {}, out);
  return out;
}

/** A message's text without the formatting marks, as a tooltip or a screen reader reads it. */
export function waPlain(text: string): string {
  return waPieces(text)
    .map((p) => p.text)
    .join("");
}

/** A piece of a message: formatted text, or a link (its text shown as written). */
export interface WaPiece extends WaSegment {
  href?: string;
}

/**
 * Links as WhatsApp finds them: an address starting with http(s):// or www., up to the next
 * space. Punctuation that ends a sentence stays outside, and a closing parenthesis only belongs
 * to the link when the link opened one.
 */
const LINK = /\b(?:https?:\/\/|www\.)[^\s<>"]+/giu;

function trimLink(raw: string): string {
  let link = raw;
  for (;;) {
    const last = link.at(-1);
    if (last && /[.,;:!?'"*_~]/.test(last)) link = link.slice(0, -1);
    else if (
      last === ")" &&
      (link.match(/\(/g)?.length ?? 0) < (link.match(/\)/g)?.length ?? 0)
    )
      link = link.slice(0, -1);
    else return link;
  }
}

/**
 * A message as WhatsApp shows it: links first (formatting never applies inside them, so an
 * address with "_" stays whole), then *bold*, _italic_, ~strike~ and monospace in the rest.
 * Only http and https addresses become links.
 */
export function waPieces(text: string): WaPiece[] {
  const out: WaPiece[] = [];
  let from = 0;
  for (const m of text.matchAll(LINK)) {
    const link = trimLink(m[0]);
    if (!link) continue;
    const at = m.index ?? 0;
    if (at > from) out.push(...waSegments(text.slice(from, at)));
    out.push({
      text: link,
      href: /^www\./i.test(link) ? `https://${link}` : link,
    });
    from = at + link.length;
  }
  if (from < text.length) out.push(...waSegments(text.slice(from)));
  return out;
}

/**
 * The lines WhatsApp lays out as blocks (help center, "Como formatar suas mensagens"): "1. "
 * (one or two digits) starts a numbered item, "* " or "- " a bulleted one and "> " a quote.
 * Following lines of the same kind join the block; any other line is text.
 */
export type WaBlock =
  | { kind: "text"; text: string }
  | { kind: "numbered"; items: Array<{ n: number; text: string }> }
  | { kind: "bulleted"; items: string[] }
  | { kind: "quote"; text: string };

export const NUMBERED_LINE = /^(\d{1,2})\. (.*)$/;
export const BULLETED_LINE = /^([-*]) (.*)$/;
export const QUOTE_LINE = /^> (.*)$/;

export function waBlocks(text: string): WaBlock[] {
  const out: WaBlock[] = [];
  for (const line of text.split("\n")) {
    const last = out.at(-1);
    let m: RegExpExecArray | null;
    if ((m = NUMBERED_LINE.exec(line))) {
      const item = { n: Number(m[1]), text: m[2]! };
      if (last?.kind === "numbered") last.items.push(item);
      else out.push({ kind: "numbered", items: [item] });
    } else if ((m = BULLETED_LINE.exec(line))) {
      if (last?.kind === "bulleted") last.items.push(m[2]!);
      else out.push({ kind: "bulleted", items: [m[2]!] });
    } else if ((m = QUOTE_LINE.exec(line))) {
      if (last?.kind === "quote") last.text += "\n" + m[1]!;
      else out.push({ kind: "quote", text: m[1]! });
    } else if (last?.kind === "text") last.text += "\n" + line;
    else out.push({ kind: "text", text: line });
  }
  return out;
}
